import api from './api';

// **INTERFACE FOR LINKED SUPPLIER (cleaned up)**
export interface LinkedSupplier {
  supplierId: string;
  name: string;
  kreditorNumber?: string;
  email: string;
  gln?: string;
  isPrimary: boolean;
  isCurrent: boolean;
  masterToken: string;
  canSwitchTo: boolean;
  supplierType: 'master' | 'linked';
}

// **RESPONSE INTERFACE**
export interface LinkedSuppliersResponse {
  success: boolean;
  linkedSuppliers: LinkedSupplier[];
  totalCount: number;
  masterSupplier: {
    _id: string;
    name: string;
    kreditorNumber?: string;
    email: string;
  };
  message?: string;
}

// **GET LINKED SUPPLIERS - COMPLETELY REBUILT**
export const getLinkedSuppliers = async (masterToken: string): Promise<LinkedSuppliersResponse> => {
  try {
    console.log('🔗 [REBUILD] Loading linked suppliers for masterToken:', masterToken);
    
    // **GET SESSION TOKEN**
    const sessionToken = sessionStorage.getItem('supplierPortalSession');
    
    if (!sessionToken) {
      console.log('❌ No session token found');
      throw new Error('Keine gültige Session gefunden');
    }
    
    console.log('📡 Making API call to linked-suppliers endpoint');
    
    // **API CALL TO BACKEND**
    const response = await api.get(`/linked-suppliers/portal/${masterToken}/linked-suppliers`, {
      headers: {
        'Authorization': `Bearer ${sessionToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('✅ Raw API response:', response.data);
    
    const data = response.data;
    
    if (!data.success) {
      return {
        success: false,
        linkedSuppliers: [],
        totalCount: 0,
        masterSupplier: { _id: '', name: '', email: '' },
        message: data.message || 'Keine Daten erhalten'
      };
    }
    
    // **FIND MASTER SUPPLIER (the one without masterSupplierId)**
    let masterSupplier = null;
    const linkedSuppliers = [];
    
    console.log('🔍 Processing suppliers to find master and linked...');
    
    for (const supplier of data.linkedSuppliers) {
      console.log('📊 Processing supplier:', {
        name: supplier.name,
        isPrimary: supplier.isPrimary,
        linkedVia: supplier.linkedVia
      });
      
      if (supplier.isPrimary || supplier.linkedVia === 'primary_account') {
        // This is the master supplier
        masterSupplier = {
          supplierId: supplier.supplierId,
          name: supplier.name,
          kreditorNumber: supplier.kreditorNumber,
          email: supplier.email,
          gln: supplier.gln,
          isPrimary: true,
          isCurrent: supplier.isCurrent || false,
          masterToken: supplier.masterToken,
          canSwitchTo: true,
          supplierType: 'master' as const
        };
        console.log('🏠 Found master supplier:', supplier.name);
      } else {
        // This is a linked supplier
        linkedSuppliers.push({
          supplierId: supplier.supplierId,
          name: supplier.name,
          kreditorNumber: supplier.kreditorNumber,
          email: supplier.email,
          gln: supplier.gln,
          isPrimary: false,
          isCurrent: supplier.isCurrent || false,
          masterToken: supplier.masterToken,
          canSwitchTo: true,
          supplierType: 'linked' as const
        });
        console.log('🔗 Found linked supplier:', supplier.name);
      }
    }
    
    // **BUILD FINAL LIST: MASTER FIRST, THEN LINKED**
    const finalSuppliers = [];
    
    if (masterSupplier) {
      finalSuppliers.push(masterSupplier);
    }
    
    finalSuppliers.push(...linkedSuppliers);
    
    console.log('✅ Final processed suppliers:', {
      master: masterSupplier?.name || 'None',
      linked: linkedSuppliers.map(s => s.name),
      total: finalSuppliers.length
    });
    
    return {
      success: true,
      linkedSuppliers: finalSuppliers,
      totalCount: finalSuppliers.length,
      masterSupplier: masterSupplier ? {
        _id: masterSupplier.supplierId,
        name: masterSupplier.name,
        kreditorNumber: masterSupplier.kreditorNumber,
        email: masterSupplier.email
      } : { _id: '', name: '', email: '' },
      message: `${finalSuppliers.length} Lieferanten geladen (1 Master, ${linkedSuppliers.length} verknüpft)`
    };
    
  } catch (error: any) {
    console.error('❌ Error in getLinkedSuppliers:', error);
    
    // **SESSION EXPIRED HANDLING**
    if (error.response?.status === 401) {
      console.log('🔄 Session expired, clearing session data');
      
      sessionStorage.removeItem('supplierPortalSession');
      sessionStorage.removeItem('supplierPortalSessionTime');
      sessionStorage.removeItem('activeSupplierContext');
      sessionStorage.removeItem('supplierInfo');
      
      // Dispatch custom event to notify GlobalDataContext of session update
      window.dispatchEvent(new CustomEvent('supplierSessionUpdate'));
      console.log('📡 Supplier session removal event dispatched');
    }
    
    return {
      success: false,
      linkedSuppliers: [],
      totalCount: 0,
      masterSupplier: { _id: '', name: '', email: '' },
      message: error.message || 'Fehler beim Laden der verknüpften Lieferanten'
    };
  }
};

// **CREATE LINKED SUPPLIER**
export const createLinkedSupplier = async (
  masterToken: string,
  supplierData: {
    name: string;
    kreditorNumber: string;
    email: string;
    gln?: string;
  }
): Promise<{ success: boolean; message: string; linkedSupplier?: LinkedSupplier }> => {
  try {
    const sessionToken = sessionStorage.getItem('supplierPortalSession');
    
    if (!sessionToken) {
      throw new Error('Keine gültige Session gefunden');
    }
    
    const response = await api.post(`/linked-suppliers/${masterToken}`, supplierData, {
      headers: {
        'Authorization': `Bearer ${sessionToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    return response.data;
    
  } catch (error: any) {
    console.error('❌ Error creating linked supplier:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Fehler beim Erstellen des verknüpften Lieferanten'
    };
  }
};

// **DELETE LINKED SUPPLIER**
export const deleteLinkedSupplier = async (
  masterToken: string,
  linkedSupplierId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const sessionToken = sessionStorage.getItem('supplierPortalSession');
    
    if (!sessionToken) {
      throw new Error('Keine gültige Session gefunden');
    }
    
    const response = await api.delete(`/linked-suppliers/${masterToken}/${linkedSupplierId}`, {
      headers: {
        'Authorization': `Bearer ${sessionToken}`
      }
    });
    
    return response.data;
    
  } catch (error: any) {
    console.error('❌ Error deleting linked supplier:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Fehler beim Löschen des verknüpften Lieferanten'
    };
  }
};

// **LEGACY FUNCTION ALIASES (for backwards compatibility)**
export const linkSuppliers = async (
  masterSupplierId: string,
  supplierIds: string[]
): Promise<{ success: boolean; message: string; results?: any[] }> => {
  try {
    console.log('🔗 Linking suppliers to master:', masterSupplierId, 'suppliers:', supplierIds);
    
    const sessionToken = sessionStorage.getItem('supplierPortalSession');
    if (!sessionToken) {
      throw new Error('Keine gültige Session gefunden');
    }
    
    // **LINK MULTIPLE SUPPLIERS TO MASTER**
    const results = [];
    
    for (const supplierId of supplierIds) {
      try {
        const response = await api.post('/linked-suppliers/link', {
          masterSupplierId,
          linkedSupplierId: supplierId
        }, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        results.push({ supplierId, success: true, data: response.data });
      } catch (error: any) {
        results.push({ 
          supplierId, 
          success: false, 
          error: error.response?.data?.message || error.message 
        });
      }
    }
    
    const successCount = results.filter(r => r.success).length;
    
    return {
      success: successCount > 0,
      message: `${successCount} von ${supplierIds.length} Lieferanten erfolgreich verknüpft`,
      results
    };
    
  } catch (error: any) {
    console.error('❌ Error linking suppliers:', error);
    return {
      success: false,
      message: error.message || 'Fehler beim Verknüpfen der Lieferanten'
    };
  }
};

export const unlinkSuppliers = async (
  masterSupplierId: string,
  supplierIds: string[]
): Promise<{ success: boolean; message: string; results?: any[] }> => {
  try {
    console.log('🔗 Unlinking suppliers from master:', masterSupplierId, 'suppliers:', supplierIds);
    
    const sessionToken = sessionStorage.getItem('supplierPortalSession');
    if (!sessionToken) {
      throw new Error('Keine gültige Session gefunden');
    }
    
    // **UNLINK MULTIPLE SUPPLIERS FROM MASTER**
    const results = [];
    
    for (const supplierId of supplierIds) {
      try {
        const response = await api.post('/linked-suppliers/unlink', {
          masterSupplierId,
          linkedSupplierId: supplierId
        }, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        results.push({ supplierId, success: true, data: response.data });
      } catch (error: any) {
        results.push({ 
          supplierId, 
          success: false, 
          error: error.response?.data?.message || error.message 
        });
      }
    }
    
    const successCount = results.filter(r => r.success).length;
    
    return {
      success: successCount > 0,
      message: `${successCount} von ${supplierIds.length} Lieferanten erfolgreich getrennt`,
      results
    };
    
  } catch (error: any) {
    console.error('❌ Error unlinking suppliers:', error);
    return {
      success: false,
      message: error.message || 'Fehler beim Trennen der Lieferanten'
    };
  }
};